import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import { usePageTitle } from '../hooks/usePageTitle'
import { useSettingsStore } from '../stores/settingsStore'
import { Play, Square, Download, Activity } from 'lucide-react'
import { HistoricalDatePicker } from '../components/ui/HistoricalDatePicker'
import { DaySummaryWidget } from '../components/oms/components/DaySummaryWidget'
import { OrderHistoryTable } from '../components/oms/components/OrderHistoryTable'
import { strategyEvaluators, strategyDisplayNames } from '../components/oms/strategies';
import { useOMS } from '../components/oms/OMSProvider'

const strategyNames = strategyDisplayNames;

import { OMSProvider } from '../components/oms/OMSProvider'
import BacktestWorker from '../workers/multiday-backtest.worker.ts?worker'
import { getFirebaseAuth } from '../services/firebase'


export const Route = createFileRoute('/_auth/backtest')({
    component: () => (
        <OMSProvider>
            <BacktestRoute />
        </OMSProvider>
    ),
})

function BacktestRoute() {
    usePageTitle('Multi-Month Backtest')

    const symbol = useSettingsStore(s => s.symbol)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [expiryIndex, setExpiryIndex] = useState<number>(0)
    const [isRunning, setIsRunning] = useState(false)
    const [progress, setProgress] = useState(0)
    const [results, setResults] = useState<any[]>([])
    const { configs } = useOMS();
    const strategyKeys = Object.keys(strategyEvaluators);
    const [selectedStrategy, setSelectedStrategy] = useState<string>(strategyKeys[0] || '');
    const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Default: Mon-Fri

    const workerRef = React.useRef<Worker | null>(null)

    const handleStartDateChange = (dateStr: string) => {
        setStartDate(dateStr)
        if (endDate && new Date(dateStr) > new Date(endDate)) {
            setEndDate(dateStr)
        }
    }

    const handleEndDateChange = (dateStr: string) => {
        setEndDate(dateStr)
        if (startDate && new Date(dateStr) < new Date(startDate)) {
            setStartDate(dateStr)
        }
    }
    const [dayLogs, setDayLogs] = useState<Array<{date: string, expiry: string, allExpiries: string[], snapshots: number}>>([])

    const startBacktest = async () => {
        if (!startDate || !endDate) {
            alert('Please select both start and end dates')
            return
        }
        if (new Date(startDate) > new Date(endDate)) {
            alert('Start Date cannot be after End Date')
            return
        }
        setIsRunning(true)
        setProgress(0)
        setResults([])
        setDayLogs([])

        if (workerRef.current) {
            workerRef.current.terminate()
        }

        // Initialize Web Worker
        workerRef.current = new BacktestWorker()

        workerRef.current.onmessage = (e) => {
            const { type, payload } = e.data
            if (type === 'PROGRESS') {
                setProgress(payload)
            } else if (type === 'DAY_INFO') {
                setDayLogs(prev => [...prev, payload])
            } else if (type === 'COMPLETE') {
                setResults(payload)
                setIsRunning(false)
            } else if (type === 'ERROR') {
                alert('Backtest Error: ' + payload)
                setIsRunning(false)
            }
        }

        const hfToken = (await getFirebaseAuth().currentUser?.getIdToken()) || "";

        workerRef.current.postMessage({
            type: 'START_BACKTEST',
            payload: { 
                symbol, 
                startDate, 
                endDate, 
                expiryIndex, 
                hfToken,
                configs,
                selectedDays,
                settings: JSON.parse(JSON.stringify(useSettingsStore.getState()))
            }
        })

        console.log(`Starting Backtest for ${symbol} from ${startDate} to ${endDate}`)
    }

    const stopBacktest = () => {
        setIsRunning(false)
        if (workerRef.current) {
            workerRef.current.terminate()
            workerRef.current = null
        }
    }

    // Cleanup worker on unmount
    React.useEffect(() => {
        return () => {
            if (workerRef.current) workerRef.current.terminate()
        }
    }, [])

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-slate-800 dark:text-white flex items-center gap-2">
                        <Activity className="w-6 h-6 text-emerald-500" />
                        Multi-Month Backtest Engine
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                        Sequentially process Hugging Face options data to test strategies over large timeframes.
                    </p>
                </div>
            </div>

            {/* Controls Panel */}
            <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-2xl border border-slate-200/50 dark:border-slate-700/50 rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-400 via-teal-500 to-blue-500 opacity-80" />
                
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end relative z-10">
                    <div className="group">
                        <label className="flex items-center gap-1.5 text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2.5 uppercase tracking-widest transition-colors group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                            Target Symbol
                        </label>
                        <div className="px-4 py-3 bg-slate-50/80 dark:bg-slate-800/50 rounded-2xl border border-slate-200/60 dark:border-slate-700/60 font-black text-slate-800 dark:text-slate-100 shadow-inner tracking-tight">
                            {symbol}
                        </div>
                    </div>
                    <div className="group">
                        <label className="flex items-center gap-1.5 text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2.5 uppercase tracking-widest transition-colors group-hover:text-blue-600 dark:group-hover:text-blue-400">
                            Start Date
                        </label>
                        <HistoricalDatePicker 
                            symbol={symbol}
                            currentDateStr={startDate}
                            onChange={handleStartDateChange}
                            className="w-full flex items-center px-4 py-3 bg-white dark:bg-slate-950/80 border border-slate-200 dark:border-slate-700/80 rounded-2xl focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500 outline-none transition-all shadow-sm dark:text-slate-100 font-bold"
                        />
                    </div>
                    <div className="group">
                        <label className="flex items-center gap-1.5 text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2.5 uppercase tracking-widest transition-colors group-hover:text-blue-600 dark:group-hover:text-blue-400">
                            End Date
                        </label>
                        <HistoricalDatePicker 
                            symbol={symbol}
                            currentDateStr={endDate}
                            onChange={handleEndDateChange}
                            className="w-full flex items-center px-4 py-3 bg-white dark:bg-slate-950/80 border border-slate-200 dark:border-slate-700/80 rounded-2xl focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500 outline-none transition-all shadow-sm dark:text-slate-100 font-bold"
                        />
                    </div>
                    <div className="group relative">
                        <label className="flex items-center gap-1.5 text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2.5 uppercase tracking-widest transition-colors group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                            Target Expiry
                        </label>
                        <select 
                            value={expiryIndex}
                            onChange={(e) => setExpiryIndex(Number(e.target.value))}
                            className="appearance-none w-full px-4 py-3 pr-10 bg-white dark:bg-slate-950/80 border border-slate-200 dark:border-slate-700/80 rounded-2xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all shadow-sm dark:text-slate-100 font-bold cursor-pointer"
                        >
                            <option value={0}>Current Expiry</option>
                            <option value={1}>2nd Expiry (Next)</option>
                            <option value={2}>3rd Expiry</option>
                            <option value={3}>4th Expiry</option>
                            <option value={4}>5th Expiry</option>
                        </select>
                        <div className="absolute right-4 bottom-3.5 pointer-events-none">
                            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {!isRunning ? (
                            <button 
                                onClick={startBacktest}
                                className="relative flex-1 flex items-center justify-center gap-2 bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-black py-3 px-6 rounded-2xl shadow-lg shadow-emerald-500/25 transition-all active:scale-[0.98] overflow-hidden group"
                            >
                                <div className="absolute inset-0 bg-white/20 translate-y-[-100%] group-hover:translate-y-[100%] transition-transform duration-500 ease-in-out" />
                                <Play className="w-5 h-5 fill-current relative z-10" />
                                <span className="relative z-10 tracking-wide">Start Engine</span>
                            </button>
                        ) : (
                            <button 
                                onClick={stopBacktest}
                                className="relative flex-1 flex items-center justify-center gap-2 bg-gradient-to-br from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 text-white font-black py-3 px-6 rounded-2xl shadow-lg shadow-rose-500/25 transition-all active:scale-[0.98] overflow-hidden group"
                            >
                                <div className="absolute inset-0 bg-white/20 translate-y-[-100%] group-hover:translate-y-[100%] transition-transform duration-500 ease-in-out" />
                                <Square className="w-5 h-5 fill-current relative z-10" />
                                <span className="relative z-10 tracking-wide">Stop</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Days Selector Row */}
                <div className="mt-6 relative z-10 border-t border-slate-200/50 dark:border-slate-800/50 pt-6">
                    <label className="flex items-center gap-1.5 text-[11px] font-black text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-widest">
                        Backtest Days of Week
                    </label>
                    <div className="flex flex-wrap gap-2 items-center">
                        {[
                            { label: 'Monday', val: 1 },
                            { label: 'Tuesday', val: 2 },
                            { label: 'Wednesday', val: 3 },
                            { label: 'Thursday', val: 4 },
                            { label: 'Friday', val: 5 },
                        ].map(day => {
                            const isSelected = selectedDays.includes(day.val);
                            return (
                                <button
                                    key={day.val}
                                    onClick={() => {
                                        setSelectedDays(prev => 
                                            prev.includes(day.val) 
                                                ? prev.filter(d => d !== day.val)
                                                : [...prev, day.val].sort()
                                        );
                                    }}
                                    className={`px-4 py-2 text-sm font-bold rounded-xl transition-all ${isSelected ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/25' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                                >
                                    {day.label}
                                </button>
                            )
                        })}
                        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-2" />
                        <button 
                            onClick={() => setSelectedDays([1, 2, 3, 4, 5])}
                            className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-slate-100 dark:bg-slate-800/50 text-slate-500 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400 transition-colors"
                        >
                            All Days
                        </button>
                        <button 
                            onClick={() => setSelectedDays([])}
                            className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-slate-100 dark:bg-slate-800/50 text-slate-500 rounded-lg hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-400 transition-colors"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {isRunning && (
                    <div className="mt-8 space-y-3">
                        <div className="flex justify-between items-center text-sm font-semibold text-slate-600 dark:text-slate-300">
                            <span>Processing Historical Data...</span>
                            <span>{progress}%</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3 overflow-hidden">
                            <div 
                                className="bg-emerald-500 h-full rounded-full transition-all duration-300 relative overflow-hidden"
                                style={{ width: `${progress}%` }}
                            >
                                <div className="absolute top-0 left-0 w-full h-full bg-white/20 animate-pulse"></div>
                            </div>
                        </div>
                        {dayLogs.length > 0 && (
                            <div className="max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 p-3 space-y-1 text-[11px] font-mono">
                                {dayLogs.map((log, i) => (
                                    <div key={i} className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                        <span className="text-emerald-500 font-bold">✓</span>
                                        <span className="text-slate-700 dark:text-slate-200 font-bold">{log.date}</span>
                                        <span>→ Expiry: <span className="text-blue-500 font-bold">{log.expiry}</span></span>
                                        <span className="text-slate-400">({log.snapshots} snapshots)</span>
                                        {log.allExpiries?.length > 1 && (
                                            <span className="text-amber-500/70">[{log.allExpiries.length} expiries in CSV]</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Results Table */}
            {results.length > 0 && (
                <div className="flex flex-col gap-6 animate-in fade-in duration-500">
                    <DaySummaryWidget providedOrders={results} />
                    
                    <div className="flex flex-col h-full w-full">
                        <div className="flex flex-wrap items-center gap-2 mb-4 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 shadow-inner">
                            {strategyKeys.map(key => {
                                const isActive = selectedStrategy === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => setSelectedStrategy(key)}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                                            isActive 
                                            ? 'bg-slate-800 text-white border-slate-800 dark:bg-slate-700 dark:border-slate-600 shadow-md' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                                        }`}
                                    >
                                        {strategyNames[key] || key}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex-1 min-h-[500px] overflow-y-auto hide-scrollbar rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/20">
                                <h2 className="text-sm font-black text-slate-800 dark:text-slate-200">
                                    {strategyNames[selectedStrategy]} Orders
                                </h2>
                                <button 
                                    onClick={() => {
                                        const strategyOrders = results.filter((o: any) => o.strategy_id === selectedStrategy);
                                        if (strategyOrders.length === 0) return;
                                        
                                        const formatCSVDate = (dateStr: string) => {
                                            if (!dateStr) return '-';
                                            const d = new Date(dateStr);
                                            if (isNaN(d.getTime())) return '-';
                                            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = months[d.getMonth()];
                                            const year = d.getFullYear();
                                            let hours = d.getHours();
                                            const ampm = hours >= 12 ? 'PM' : 'AM';
                                            hours = hours % 12;
                                            hours = hours ? hours : 12;
                                            const mins = String(d.getMinutes()).padStart(2, '0');
                                            const secs = String(d.getSeconds()).padStart(2, '0');
                                            return `${day}-${month}-${year} ${hours}:${mins}:${secs} ${ampm}`;
                                        };

                                        const headers = ["SL No", "Date & Time", "Type", "Strike", "Option Type", "Entry Price", "Qty", "Capital", "Entry Remarks", "Exit Date & Time", "Exit Price", "Exit Capital", "Exit Remarks", "Order PL", "Charges", "Net PL"];
                                        const csvRows = strategyOrders.map((o: any, idx: number) => {
                                            const capital = o.entry_price * o.qty;
                                            const exitPrice = o.exit_price || 0;
                                            const exitCapital = exitPrice ? exitPrice * o.qty : 0;
                                            const orderPl = exitCapital ? (exitCapital - capital) : (o.order_pl || 0);
                                            const netPl = exitCapital ? (orderPl - o.charges) : (o.net_pl || 0);
                                            
                                            let strikeVal = o.strike || '';
                                            let optType = o.option_type || '';
                                            if (!optType && typeof strikeVal === 'string') {
                                                if (strikeVal.includes('CE')) {
                                                    optType = 'CE';
                                                    strikeVal = strikeVal.replace(/ CE/g, '').trim();
                                                } else if (strikeVal.includes('PE')) {
                                                    optType = 'PE';
                                                    strikeVal = strikeVal.replace(/ PE/g, '').trim();
                                                }
                                            }
                                            
                                            return [
                                                idx + 1,
                                                formatCSVDate(o.entry_time),
                                                o.trade_type,
                                                strikeVal,
                                                optType,
                                                o.entry_price.toFixed(2),
                                                o.qty,
                                                capital.toFixed(2),
                                                `"${(o.entry_remarks || '').replace(/"/g, '""')}"`,
                                                o.exit_time ? formatCSVDate(o.exit_time) : 'OPEN',
                                                exitPrice ? exitPrice.toFixed(2) : '-',
                                                exitCapital ? exitCapital.toFixed(2) : '-',
                                                `"${(o.exit_remarks || '').replace(/"/g, '""')}"`,
                                                orderPl ? orderPl.toFixed(2) : '-',
                                                Number(o.charges || 0).toFixed(2),
                                                o.net_pnl !== undefined ? Number(o.net_pnl || 0).toFixed(2) : '-'
                                            ];
                                        });

                                        const csvContent = [headers.join(","), ...csvRows.map((r: any[]) => r.join(","))].join("\n");
                                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                        const link = document.createElement('a');
                                        const url = URL.createObjectURL(blob);
                                        link.setAttribute('href', url);
                                        link.setAttribute('download', `${strategyNames[selectedStrategy]}_Backtest_Orders.csv`);
                                        link.style.visibility = 'hidden';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold rounded-lg border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                                >
                                    <Download size={14} /> Download Excel
                                </button>
                            </div>
                            <OrderHistoryTable selectedStrategyId={selectedStrategy} providedOrders={results} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
